import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { adminHeaders } from "@/components/admin/AdminShell";
import { Loader2, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const productionDefaults = () => ({
  contract_type: "production",
  quote_id: null,
  client_name: "",
  client_email: "",
  client_phone: "",
  client_organization: "",
  client_address: "",
  event_name: "",
  event_dates: "",
  event_location: "",
  selected_services: [],
  additional_services: "",
  service_role: "",
  service_time: "",
  arrival_time: "",
  service_length: "",
  late_cancellation_fee: 75,
  cancellation_window_days: 7,
  total_fee: "",
  deposit: "",
  remaining_balance: "",
  overtime_rate: "",
  revision_rate: "",
  revisions_included: 2,
  revision_window_days: 7,
  payment_timing: "before_delivery",
  payment_timing_other: "",
  delivery_formats: [],
  delivery_method: "Google Drive",
  delivery_method_other: "",
  delivery_timeline: "",
  preferred_credit: "Recorded by OneSound Productions, LLC",
});

const musicianDefaults = () => ({
  ...productionDefaults(),
  contract_type: "musician_single_service",
  service_role: "Musician",
  preferred_credit: "",
  payment_timing: "on_event",
});

const emptyForm = productionDefaults;

export default function ContractDialog({ open, onOpenChange, prefill, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [meta, setMeta] = useState({
    fixed_services: [],
    delivery_formats: [],
    delivery_methods: [],
    payment_timing: {},
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = (prefill?.contract_type === "musician_single_service")
      ? musicianDefaults()
      : productionDefaults();
    setForm({ ...base, ...(prefill || {}) });
  }, [open, prefill]);

  const fetchMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contracts/meta`);
      setMeta(res.data);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleArr = (k, value) =>
    setForm((p) => ({
      ...p,
      [k]: p[k].includes(value) ? p[k].filter((x) => x !== value) : [...p[k], value],
    }));

  const numericOrNull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return isNaN(n) ? null : n;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.client_email) {
      toast.error("Client name and email are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        client_phone: form.client_phone || null,
        client_organization: form.client_organization || null,
        client_address: form.client_address || null,
        event_name: form.event_name || null,
        event_dates: form.event_dates || null,
        event_location: form.event_location || null,
        additional_services: form.additional_services || null,
        service_role: form.service_role || null,
        service_time: form.service_time || null,
        arrival_time: form.arrival_time || null,
        service_length: form.service_length || null,
        late_cancellation_fee: numericOrNull(form.late_cancellation_fee),
        cancellation_window_days: Number(form.cancellation_window_days) || 7,
        total_fee: numericOrNull(form.total_fee),
        deposit: numericOrNull(form.deposit),
        remaining_balance: numericOrNull(form.remaining_balance),
        overtime_rate: numericOrNull(form.overtime_rate),
        revision_rate: numericOrNull(form.revision_rate),
        revisions_included: Number(form.revisions_included) || 0,
        revision_window_days: Number(form.revision_window_days) || 0,
        payment_timing_other: form.payment_timing_other || null,
        delivery_method: form.delivery_method || null,
        delivery_method_other: form.delivery_method_other || null,
        delivery_timeline: form.delivery_timeline || null,
        preferred_credit: form.preferred_credit || null,
      };
      const res = await axios.post(`${API}/contracts`, payload, { headers: adminHeaders() });
      toast.success("Contract sent to client.");
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create contract.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl flex items-center gap-3">
            <FileText size={20} /> New Contract
          </DialogTitle>
          <p className="text-white/50 text-sm font-body">
            Fill in the details. We'll email the client a link to sign in their browser and send you a copy of the signed PDF afterward.
          </p>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6">
          {/* Type selector */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { v: "production", label: "Production Services", desc: "Live / studio recording, mixing, mastering, production" },
              { v: "musician_single_service", label: "Musician — Single Service", desc: "Church looking for a musician for one service" },
            ].map((opt) => {
              const active = form.contract_type === opt.v;
              return (
                <button
                  type="button"
                  key={opt.v}
                  onClick={() => {
                    const base = opt.v === "musician_single_service" ? musicianDefaults() : productionDefaults();
                    setForm((p) => ({
                      ...base,
                      // preserve client info already entered
                      client_name: p.client_name,
                      client_email: p.client_email,
                      client_phone: p.client_phone,
                      client_organization: p.client_organization,
                      client_address: p.client_address,
                    }));
                  }}
                  className={`text-left p-4 rounded-2xl border transition-all ${
                    active
                      ? "border-brand-magenta bg-brand-magenta/10"
                      : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                  }`}
                >
                  <div className="font-display font-extrabold text-sm text-white">{opt.label}</div>
                  <div className="font-body text-xs text-white/55 mt-1">{opt.desc}</div>
                </button>
              );
            })}
          </div>

          <Section title="Client">
            <Grid2>
              <Field label="Name *" value={form.client_name} onChange={(v) => update("client_name", v)} />
              <Field label="Email *" value={form.client_email} onChange={(v) => update("client_email", v)} type="email" />
              <Field label="Phone" value={form.client_phone} onChange={(v) => update("client_phone", v)} />
              <Field label="Church / Organization" value={form.client_organization} onChange={(v) => update("client_organization", v)} />
            </Grid2>
            <Field label="Address" value={form.client_address} onChange={(v) => update("client_address", v)} />
          </Section>

          {form.contract_type === "musician_single_service" ? (
            <>
              <Section title="Service Details">
                <Grid2>
                  <Field label="Service Role *" value={form.service_role} onChange={(v) => update("service_role", v)} placeholder="e.g. Keyboardist, MD" />
                  <Field label="Event / Service Name" value={form.event_name} onChange={(v) => update("event_name", v)} placeholder="Sunday Morning Worship" />
                  <Field label="Date of Service" value={form.event_dates} onChange={(v) => update("event_dates", v)} placeholder="January 12, 2026" />
                  <Field label="Service Start Time" value={form.service_time} onChange={(v) => update("service_time", v)} placeholder="11:00 AM" />
                  <Field label="Arrival / Soundcheck" value={form.arrival_time} onChange={(v) => update("arrival_time", v)} placeholder="9:30 AM" />
                  <Field label="Estimated Length" value={form.service_length} onChange={(v) => update("service_length", v)} placeholder="2 hours" />
                </Grid2>
                <Field label="Location" value={form.event_location} onChange={(v) => update("event_location", v)} placeholder="Church name + address" />
              </Section>

              <Section title="Fees & Non-Refundable Deposit">
                <Grid2>
                  <Field label="Total Fee" value={form.total_fee} onChange={(v) => update("total_fee", v)} placeholder="0.00" type="number" />
                  <Field label="Non-Refundable Deposit" value={form.deposit} onChange={(v) => update("deposit", v)} placeholder="0.00" type="number" />
                  <Field label="Balance Due" value={form.remaining_balance} onChange={(v) => update("remaining_balance", v)} placeholder="0.00" type="number" />
                  <Field label="Overtime Rate (per hour)" value={form.overtime_rate} onChange={(v) => update("overtime_rate", v)} placeholder="0.00" type="number" />
                </Grid2>

                <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60 mt-2 block">
                  Balance due
                </Label>
                <Select value={form.payment_timing} onValueChange={(v) => update("payment_timing", v)}>
                  <SelectTrigger className="bg-black/40 border-white/10 text-white h-12 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-white/10 text-white">
                    {Object.entries(meta.payment_timing).map(([k, v]) => (
                      <SelectItem key={k} value={k} className="focus:bg-white/10 focus:text-white">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Section>

              <Section title="Cancellation Policy">
                <Grid2>
                  <Field
                    label="Cancellation Window (days before event with no penalty)"
                    value={form.cancellation_window_days}
                    onChange={(v) => update("cancellation_window_days", v)}
                    type="number"
                    placeholder="7"
                  />
                  <Field
                    label="Late-Cancellation Fee (within window or same-day)"
                    value={form.late_cancellation_fee}
                    onChange={(v) => update("late_cancellation_fee", v)}
                    type="number"
                    placeholder="75"
                  />
                </Grid2>
                <p className="text-xs text-white/50 leading-relaxed">
                  Default: client can cancel up to 7 days before the service with no
                  penalty (deposit forfeited). Inside that window — or same-day no-show
                  — a $75 late fee applies on top of deposit forfeiture.
                </p>
              </Section>
            </>
          ) : (
            <>
              <Section title="Project">
                <Grid2>
                  <Field label="Event / Project Name" value={form.event_name} onChange={(v) => update("event_name", v)} />
                  <Field label="Date(s) of Service" value={form.event_dates} onChange={(v) => update("event_dates", v)} placeholder="e.g. April 20, 2026" />
                </Grid2>
                <Field label="Location" value={form.event_location} onChange={(v) => update("event_location", v)} />

                <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60 mt-2 block">
                  Selected Services
                </Label>
                <div className="grid sm:grid-cols-2 gap-2 mt-2">
                  {meta.fixed_services.map((s) => (
                    <label
                      key={s}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.02] cursor-pointer hover:bg-white/[0.05]"
                    >
                      <Checkbox
                        checked={form.selected_services.includes(s)}
                        onCheckedChange={() => toggleArr("selected_services", s)}
                      />
                      <span className="text-sm font-body">{s}</span>
                    </label>
                  ))}
                </div>
                <Field label="Additional / Custom Services (optional)" value={form.additional_services} onChange={(v) => update("additional_services", v)} />
              </Section>

              <Section title="Fees">
                <Grid2>
                  <Field label="Total Project Fee" value={form.total_fee} onChange={(v) => update("total_fee", v)} placeholder="0.00" type="number" />
                  <Field label="Deposit / Retainer" value={form.deposit} onChange={(v) => update("deposit", v)} placeholder="0.00" type="number" />
                  <Field label="Remaining Balance" value={form.remaining_balance} onChange={(v) => update("remaining_balance", v)} placeholder="0.00" type="number" />
                  <Field label="Overtime Rate (per hour)" value={form.overtime_rate} onChange={(v) => update("overtime_rate", v)} placeholder="0.00" type="number" />
                </Grid2>

                <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60 mt-2 block">
                  Remaining balance due
                </Label>
                <Select
                  value={form.payment_timing}
                  onValueChange={(v) => update("payment_timing", v)}
                >
                  <SelectTrigger className="bg-black/40 border-white/10 text-white h-12 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-white/10 text-white">
                    {Object.entries(meta.payment_timing).map(([k, v]) => (
                      <SelectItem key={k} value={k} className="focus:bg-white/10 focus:text-white">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.payment_timing === "other" && (
                  <Field label="Other (describe)" value={form.payment_timing_other} onChange={(v) => update("payment_timing_other", v)} />
                )}
              </Section>

              <Section title="Revisions">
                <Grid2>
                  <Field label="Revisions Included" value={form.revisions_included} onChange={(v) => update("revisions_included", v)} type="number" />
                  <Field label="Revision Window (days)" value={form.revision_window_days} onChange={(v) => update("revision_window_days", v)} type="number" />
                  <Field label="Additional Revision Rate (per hour/revision)" value={form.revision_rate} onChange={(v) => update("revision_rate", v)} type="number" />
                </Grid2>
              </Section>

              <Section title="Delivery">
                <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60 block">
                  Final File Formats
                </Label>
                <div className="grid sm:grid-cols-3 gap-2 mt-2">
                  {meta.delivery_formats.map((f) => (
                    <label
                      key={f}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-white/10 bg-white/[0.02] cursor-pointer hover:bg-white/[0.05]"
                    >
                      <Checkbox
                        checked={form.delivery_formats.includes(f)}
                        onCheckedChange={() => toggleArr("delivery_formats", f)}
                      />
                      <span className="text-sm font-body">{f}</span>
                    </label>
                  ))}
                </div>

                <Grid2>
                  <div>
                    <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60 block">
                      Delivery Method
                    </Label>
                    <Select
                      value={form.delivery_method}
                      onValueChange={(v) => update("delivery_method", v)}
                    >
                      <SelectTrigger className="bg-black/40 border-white/10 text-white h-12 rounded-xl mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-white/10 text-white">
                        {meta.delivery_methods.map((m) => (
                          <SelectItem key={m} value={m} className="focus:bg-white/10 focus:text-white">
                            {m}
                          </SelectItem>
                        ))}
                        <SelectItem value="Other" className="focus:bg-white/10 focus:text-white">
                          Other
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Field label="Estimated Delivery Timeline" value={form.delivery_timeline} onChange={(v) => update("delivery_timeline", v)} placeholder="e.g. 21 business days after the event" />
                </Grid2>
                {form.delivery_method === "Other" && (
                  <Field label="Other delivery method" value={form.delivery_method_other} onChange={(v) => update("delivery_method_other", v)} />
                )}
              </Section>

              <Section title="Credits">
                <Field label="Preferred Credit (Produced / Recorded by)" value={form.preferred_credit} onChange={(v) => update("preferred_credit", v)} />
              </Section>
            </>
          )}

          <DialogFooter className="gap-2 sticky bottom-0 bg-zinc-950 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-full border-white/10 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-full bg-white text-black hover:bg-brand-magenta hover:text-white font-display font-bold"
            >
              {saving ? (
                <Loader2 size={14} className="animate-spin mr-2" />
              ) : null}
              {saving ? "Sending..." : "Create & Send Contract"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <p className="font-body text-[11px] uppercase tracking-[0.25em] text-brand-magenta">
        {title}
      </p>
      {children}
    </div>
  );
}

function Grid2({ children }) {
  return <div className="grid sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, value, onChange, type = "text", placeholder = "" }) {
  if (type === "textarea") {
    return (
      <div className="space-y-1.5">
        <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60">{label}</Label>
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="bg-black/40 border-white/10 text-white rounded-xl focus-visible:ring-brand-magenta"
        />
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-black/40 border-white/10 text-white h-12 rounded-xl focus-visible:ring-brand-magenta"
      />
    </div>
  );
}
